import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { SITE_URL, orgSchema, breadcrumb, faqSchema } from "@/lib/seo";
import { resolveSeo, ALL_LOCATIONS, SEO_LANGS, BASE_LANGS, RTL_LANGS, SEO_I18N, kwLabel as kwLabelFn, kwLead as kwLeadFn } from "@/data/seoEngine";
import { WHATSAPP_URL } from "@/lib/contact";
import { ArrowLeft, ArrowRight, Path, Clock, CurrencyEur, Airplane, CheckCircle } from "@phosphor-icons/react";

// SeoLanding-specific UI strings (independent of the global DE/EN app i18n).
const UI = {
  de: { price_from: "Festpreis ab ZRH", distance: "Distanz ab ZRH", eta: "Ca. Fahrzeit", book: "Diesen Transfer buchen", faq: "Häufig gestellte Fragen", more: "Weitere Ziele", on_request: "Auf Anfrage", quote: "Angebot anfragen", hub_tag: "Festpreis, 24/7, alle Ziele", benefits: ["Festpreis, keine versteckten Kosten", "Meet & Greet in der Ankunftshalle", "Gratis Wartezeit bei Flugverspätung", "24/7 verfügbar, 365 Tage"] },
  en: { price_from: "Fixed fare from ZRH", distance: "Distance from ZRH", eta: "Approx. drive", book: "Book this transfer", faq: "Frequently asked questions", more: "More destinations", on_request: "On request", quote: "Request a quote", hub_tag: "Fixed fare, 24/7, all destinations", benefits: ["Fixed fare, no hidden costs", "Meet & greet in arrivals", "Free wait time on flight delays", "Available 24/7, 365 days"] },
  fr: { price_from: "Prix fixe depuis ZRH", distance: "Distance depuis ZRH", eta: "Durée env.", book: "Réserver ce transfert", faq: "Questions fréquentes", more: "Autres destinations", on_request: "Sur demande", quote: "Demander une offre", hub_tag: "Prix fixe, 24h/24, toutes destinations", benefits: ["Prix fixe, sans frais cachés", "Accueil dans le hall des arrivées", "Temps d'attente gratuit en cas de retard", "Disponible 24h/24, 365 jours"] },
  it: { price_from: "Prezzo fisso da ZRH", distance: "Distanza da ZRH", eta: "Durata circa", book: "Prenota questo transfer", faq: "Domande frequenti", more: "Altre destinazioni", on_request: "Su richiesta", quote: "Richiedi un preventivo", hub_tag: "Prezzo fisso, 24/7, tutte le destinazioni", benefits: ["Prezzo fisso, senza costi nascosti", "Accoglienza nell'area arrivi", "Attesa gratuita in caso di ritardo", "Disponibile 24 ore su 24, 365 giorni"] },
};

const uiFor = (pl) => (BASE_LANGS.includes(pl) ? UI[pl] : { ...UI.en, ...(SEO_I18N[pl]?.ui || {}), benefits: SEO_I18N[pl]?.benefits || UI.en.benefits });

function buildFaqs(pl, kwLabel, name, fare, price) {
  if (!["de", "en", "fr", "it"].includes(pl)) {
    const f = SEO_I18N[pl]?.faqs;
    const fill = (s) => (s || "").split("{k}").join(kwLabel).split("{c}").join(name).split("{p}").join(price).split("{m}").join(fare ? fare.eta_minutes : "").split("{km}").join(fare ? fare.distance_km : "");
    if (f) return [
      { q: fill(f.q1), a: fare ? fill(f.a1) : fill(f.a1n) },
      { q: fill(f.q2), a: fare ? fill(f.a2) : fill(f.a2n) },
      { q: fill(f.q3), a: fill(f.a3) },
    ];
  }
  const kl = kwLabel.toLowerCase();
  if (pl === "fr") return [
    { q: `Combien coûte ${kl} de Zurich à ${name} ?`, a: fare ? `Le prix fixe démarre à ${price} – transparent, sans taximètre ni frais cachés.` : `Vous obtenez un prix fixe transparent avant de réserver.` },
    { q: `Combien de temps dure le trajet vers ${name} ?`, a: fare ? `Environ ${fare.eta_minutes} minutes (${fare.distance_km} km), selon le trafic et la météo.` : `La durée est indiquée lors de la réservation.` },
    { q: `Puis-je réserver ${kl} vers ${name} à l'avance ?`, a: `Oui, réservez en ligne. Nous suivons votre vol et adaptons l'heure de prise en charge en cas de retard.` },
  ];
  if (pl === "it") return [
    { q: `Quanto costa ${kl} da Zurigo a ${name}?`, a: fare ? `Il prezzo fisso parte da ${price} – trasparente, senza tassametro né costi nascosti.` : `Ricevete un prezzo fisso trasparente prima di prenotare.` },
    { q: `Quanto dura il tragitto verso ${name}?`, a: fare ? `Circa ${fare.eta_minutes} minuti (${fare.distance_km} km), a seconda di traffico e meteo.` : `La durata è indicata al momento della prenotazione.` },
    { q: `Posso prenotare ${kl} verso ${name} in anticipo?`, a: `Sì, prenotate online. Monitoriamo il vostro volo e adattiamo l'orario di ritiro in caso di ritardo.` },
  ];
  if (pl === "en") return [
    { q: `How much is ${kl} from Zürich to ${name}?`, a: fare ? `The fixed fare starts from ${price} – transparent, no meter and no hidden costs.` : `You get a transparent fixed fare before booking.` },
    { q: `How long is the drive to ${name}?`, a: fare ? `About ${fare.eta_minutes} minutes (${fare.distance_km} km), depending on traffic and weather.` : `Travel time is shown at booking.` },
    { q: `Can I book ${kl} to ${name} in advance?`, a: `Yes, book online. We monitor your flight and adjust the pickup time on delays.` },
  ];
  return [
    { q: `Was kostet ${kwLabel} von Zürich nach ${name}?`, a: fare ? `Der Festpreis beträgt ab ${price} – transparent, ohne Taxameter und ohne versteckte Kosten.` : `Sie erhalten einen transparenten Festpreis vor der Buchung.` },
    { q: `Wie lange dauert die Fahrt nach ${name}?`, a: fare ? `Ca. ${fare.eta_minutes} Minuten (${fare.distance_km} km), abhängig von Verkehr und Wetter.` : `Die Fahrzeit sehen Sie bei der Buchung.` },
    { q: `Kann ich ${kwLabel} nach ${name} im Voraus buchen?`, a: `Ja, buchen Sie bequem online. Wir überwachen Ihren Flug und passen die Abholzeit bei Verspätungen an.` },
  ];
}

const pathFor = (lang, slug) => (lang === "de" ? `/${slug}` : `/${lang}/${slug}`);

export default function SeoLanding({ pageLang = "de" }) {
  const { seoSlug } = useParams();
  const navigate = useNavigate();
  const pl = SEO_LANGS.includes(pageLang) ? pageLang : "de";
  const ui = uiFor(pl);
  const res = resolveSeo(seoSlug);
  const [fare, setFare] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!res) { navigate("/", { replace: true }); return; }
    if (res.type === "city" && res.loc.cityId) {
      api.post("/fare", { pickup_id: "zrh-airport", dropoff_id: res.loc.cityId, vehicle_class: "standard", time: "10:00" })
        .then((r) => setFare(r.data)).catch(() => setFare(null));
    }
  }, [seoSlug, pl]); // eslint-disable-line

  if (!res) return null;
  const kwLabel = kwLabelFn(res.kw, pl);
  const zn = ({ de: "Zürich", en: "Zürich", fr: "Zurich", it: "Zurigo" })[pl] || "Zürich";
  const rtl = RTL_LANGS.includes(pl);

  const altsFor = (slug) => [
    ...SEO_LANGS.map((l) => ({ hreflang: l, href: `${SITE_URL}${pathFor(l, slug)}` })),
    { hreflang: "x-default", href: `${SITE_URL}${pathFor("de", slug)}` },
  ];

  // HUB PAGE
  if (res.type === "hub") {
    const hubSlug = res.kw.slug;
    const title = `${kwLabel} ${zn} | ${ui.hub_tag}`;
    const desc = kwLeadFn(res.kw, pl, pl === "de" ? "Ihrem Ziel" : pl === "fr" ? "votre destination" : pl === "it" ? "la vostra destinazione" : "your destination");
    return (
      <div dir={rtl ? "rtl" : "ltr"} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="seo-hub-page">
        <Seo title={title} description={desc} path={pathFor(pl, hubSlug)} alternates={altsFor(hubSlug)} jsonLd={[orgSchema(), breadcrumb([{ name: "Home", path: "/" }, { name: kwLabel, path: pathFor(pl, hubSlug) }])]} />
        <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-[#0B1533] leading-tight" data-testid="seo-h1">{kwLabel} {zn}</h1>
        <p className="font-body text-lg text-[#3A3F55] leading-relaxed mt-5 max-w-3xl">{desc}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-10">
          {ALL_LOCATIONS.map((l) => (
            <Link key={l.slug} to={pathFor(pl, `${hubSlug}-${l.slug}`)} className="group flex items-center justify-between gap-3 rounded-2xl border border-[#E9E5DE] bg-white px-5 py-4 hover:border-[#EF5A1E] transition-colors duration-200" data-testid={`hub-link-${l.slug}`}>
              <span className="flex items-center gap-2 font-body text-sm text-[#0B1533]"><Airplane size={15} className="text-[#EF5A1E]" /> {kwLabel} {l.name}</span>
              <ArrowRight size={15} className="text-[#9CA0B0] group-hover:text-[#EF5A1E] group-hover:translate-x-1 transition-all duration-200" />
            </Link>
          ))}
        </div>
      </div>
    );
  }

  // CITY PAGE
  const name = res.loc.name;
  const noFare = !res.loc.cityId;
  const price = fare ? `CHF ${fare.total.toFixed(0)}` : noFare ? ui.on_request : "…";
  const pageSlug = `${res.kw.slug}-${res.loc.slug}`;
  const title = `${kwLabel} ${zn} – ${name} | ${ui.price_from}`;
  const fromTxt = fare ? (pl === "de" ? `Ab ${price}.` : pl === "fr" ? `Dès ${price}.` : pl === "it" ? `Da ${price}.` : `From ${price}.`) : "";
  const desc = `${kwLeadFn(res.kw, pl, name)} ${fromTxt}`.trim();
  const faqs = buildFaqs(pl, kwLabel, name, fare, price);
  const today = new Date().toISOString().slice(0, 10);
  const book = () => navigate("/book", { state: { service_type: "transfer", pickup_id: "zrh-airport", pickup_name: "Flughafen Zürich (ZRH)", dropoff_id: res.loc.cityId, dropoff_name: name, date: today, time: "10:00" } });
  const related = ALL_LOCATIONS.filter((l) => l.slug !== res.loc.slug).slice(0, 6);

  return (
    <div dir={rtl ? "rtl" : "ltr"} className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="seo-city-page">
      <Seo title={title} description={desc} path={pathFor(pl, pageSlug)} alternates={altsFor(pageSlug)}
        jsonLd={[orgSchema(),
          breadcrumb([{ name: "Home", path: "/" }, { name: kwLabel, path: pathFor(pl, res.kw.slug) }, { name, path: pathFor(pl, pageSlug) }]),
          { "@context": "https://schema.org", "@type": "Service", serviceType: kwLabel, name: `${kwLabel} ${zn} → ${name}`, provider: { "@type": "TaxiService", name: "Airport Transfer Zurich" }, areaServed: { "@type": "City", name }, ...(fare ? { offers: { "@type": "Offer", price: fare.total.toFixed(2), priceCurrency: "CHF" } } : {}) },
          faqSchema(faqs)]} />

      <Link to={pathFor(pl, res.kw.slug)} className="inline-flex items-center gap-2 font-body text-sm text-[#5A607A] hover:text-[#0B1533] transition-colors duration-200 mb-6"><ArrowLeft size={15} /> {kwLabel} {zn}</Link>
      <div className="flex items-center gap-2.5 font-body text-sm text-[#EF5A1E] font-semibold mb-3">
        <Airplane size={16} weight="fill" /> {zn} (ZRH) <ArrowRight size={13} /> {name}
      </div>
      <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-[#0B1533] leading-tight" data-testid="seo-h1">{kwLabel} {zn} – {name}</h1>
      <p className="font-body text-lg text-[#3A3F55] leading-relaxed mt-6 max-w-3xl">{kwLeadFn(res.kw, pl, name)}</p>

      <div className="grid grid-cols-3 gap-4 mt-10">
        {[
          { icon: Path, label: ui.distance, val: fare ? `${fare.distance_km} km` : noFare ? "—" : "…" },
          { icon: Clock, label: ui.eta, val: fare ? `~${fare.eta_minutes} min` : noFare ? "—" : "…" },
          { icon: CurrencyEur, label: ui.price_from, val: price },
        ].map((s, i) => (
          <div key={i} className="rounded-2xl border border-[#E9E5DE] bg-white p-5" data-testid={`seo-stat-${i}`}>
            <s.icon size={20} className="text-[#EF5A1E]" />
            <p className="font-heading font-bold text-2xl tracking-tight text-[#0B1533] mt-3">{s.val}</p>
            <p className="font-body text-[10px] text-[#5A607A] uppercase tracking-wider mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-3xl bg-[#0B1533] p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
        <div className="flex items-center gap-3">
          <Airplane size={28} weight="fill" className="text-[#EF5A1E]" />
          <div>
            <p className="font-body text-xs text-white/50 uppercase tracking-wider">{ui.price_from}</p>
            <p className="font-heading font-bold text-2xl text-white" data-testid="seo-price">{fare ? `CHF ${fare.total.toFixed(2)}` : noFare ? ui.on_request : "…"}</p>
          </div>
        </div>
        {noFare ? (
          <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="seo-quote-btn" className="inline-flex items-center gap-2 bg-[#EF5A1E] text-white font-body font-semibold rounded-full px-8 py-4 hover:bg-[#D2450F] transition-colors duration-200">
            {ui.quote} <ArrowRight size={16} />
          </a>
        ) : (
          <button onClick={book} data-testid="seo-book-btn" className="inline-flex items-center gap-2 bg-[#EF5A1E] text-white font-body font-semibold rounded-full px-8 py-4 hover:bg-[#D2450F] transition-colors duration-200">
            {ui.book} <ArrowRight size={16} />
          </button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-10">
        {ui.benefits.map((b, i) => (
          <div key={i} className="flex items-center gap-3 rounded-2xl bg-[#F5F2ED] px-5 py-4">
            <CheckCircle size={20} weight="fill" className="text-[#EF5A1E] shrink-0" />
            <span className="font-body text-sm text-[#0B1533]">{b}</span>
          </div>
        ))}
      </div>

      <div className="mt-12">
        <h2 className="font-heading font-bold text-2xl tracking-tight text-[#0B1533] mb-5">{ui.faq}</h2>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <div key={i} className="rounded-2xl border border-[#E9E5DE] bg-white p-5" data-testid={`seo-faq-${i}`}>
              <p className="font-body font-semibold text-[#0B1533]">{f.q}</p>
              <p className="font-body text-sm text-[#5A607A] mt-2 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12">
        <h2 className="font-heading font-bold text-2xl tracking-tight text-[#0B1533] mb-5">{ui.more}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {related.map((l) => (
            <Link key={l.slug} to={pathFor(pl, `${res.kw.slug}-${l.slug}`)} className="group flex items-center justify-between gap-3 rounded-2xl border border-[#E9E5DE] bg-white px-5 py-4 hover:border-[#EF5A1E] transition-colors duration-200">
              <span className="flex items-center gap-2 font-body text-sm text-[#0B1533]"><Airplane size={15} className="text-[#EF5A1E]" /> {kwLabel} {l.name}</span>
              <ArrowRight size={15} className="text-[#9CA0B0] group-hover:text-[#EF5A1E] group-hover:translate-x-1 transition-all duration-200" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
